import { FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { Progress } from '../../../interfaces/Progress'
import { AddProgress } from '../../../components/Progresses/AddProgress'
import { DeleteProgress } from '../../../components/Progresses/DeleteProgress'
import { UpdateProgress } from '../../../components/Progresses/UpdateProgress'
import useUser from '../../../hooks/User'
import { IconButton, Menu, MenuItem } from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { useIntl } from 'react-intl'
import { useCustomeStore } from '../../../stores/CustomeStore'
import { CellContext } from '@tanstack/react-table'
import useDynamicTab from '../../../hooks/useDynamicTab'
import useModalEffect from '../../../hooks/useModalEffect'
import { useDataTableStore } from '../../../stores/DataTableStore'
import { mutate } from 'swr'
import { API_URL } from '../../../api/SettingCRUD'
import {
  CRMColumnDef,
  MasterDataTableTanstack,
} from '../../../components/DataTable/MasterDataTableTanstack'
import { FilterProgresses } from './FilterProgresses'
import moment from 'moment-timezone'
import { usePipelineStore } from '../../../stores/PipelineStore'
import { useSettingsStore } from '../../../stores/SettingsStore'

const tableKey = 'lead_progresses'
const table_URL = 'lead_progresses'

const MasterProgresses: FC = () => {
  const intl = useIntl()
  const user = useUser()
  const { progressId, progressName } = useCustomeStore()
  const { settings } = useSettingsStore()
  useDynamicTab({ title: `${intl.formatMessage({ id: 'FORM.MENU.PROGRESSES' })} | CRM WooBlazz` })

  const {
    setTableColumnVisibility,
    setTableColumns,
    isFullyLoaded,
  } = useDataTableStore()
  const [manageColumns, setManageColumns] = useState<CRMColumnDef<Progress>[]>([])
  const { multiPipelines } = usePipelineStore()
  const [showUpdateModal, setShowUpdateModal] = useState<boolean>(false)
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)
  const [progress, setProgress] = useState<Progress>()
  const [openMenuId, setOpenMenuId] = useState<string | null>(null)

  const anchorRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({})

  useModalEffect({
    showModal: showUpdateModal,
    setShowModal: setShowUpdateModal,
    modalId: `update-progress-modal-${progress?.id}`,
  })

  useModalEffect({
    showModal: showDeleteModal,
    setShowModal: setShowDeleteModal,
    modalId: `delete-progress-modal-${progress?.id}`,
  })

  const handleClick = (menuId: string) => {
    setOpenMenuId(menuId)
  }
  const handleClose = () => {
    setOpenMenuId(null)
  }

  useEffect(() => {
    setManageColumns(columns)
    setTableColumnVisibility(tableKey, (prev) => {
      const newState = { ...prev }
      columns.forEach((column) => {
        newState[column.id ?? '???'] = true
      })
      return newState
    })
    setTableColumns(tableKey, columns)
  }, [])

  // const columns: ColumnDef<Progress>[] = [
  const columns = useMemo<CRMColumnDef<Progress>[]>(
    () => [
      {
        id: 'actions',
        header: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.ACTION' }),
        meta: {
          size: 1,
          maxSize: 1,
          styleHeader: {
            alignContent: 'center',
            textAlign: 'center',
          },
          styleBody: {
            alignContent: 'center',
            textAlign: 'center',
            width: '30px',
            overflow: 'auto',
          },
          isPinnedLeft: true,
          disableRowFn: true,
        },
        cell: (props: CellContext<Progress, unknown>) => {
          const row = props.row.original
          return (
            <>
              {row.name.toLowerCase() !== progressName.newSales &&
                row.name.toLowerCase() !== progressName.newService &&
                row.id !== progressId.newPipeline && (
                  <>
                    <IconButton
                      aria-label={`more-${row.id}`}
                      id={`menu-anchor-${row.id}`}
                      onClick={() => handleClick(row.id)}
                      ref={(el) => {
                        anchorRefs.current[row.id] = el
                      }}
                    >
                      <MoreVertIcon />
                    </IconButton>
                    <Menu
                      id={`menu-${row.id}`}
                      anchorEl={anchorRefs.current[row.id]}
                      open={openMenuId === row.id}
                      onClose={handleClose}
                      disableScrollLock={true}
                    >
                      {(user.data.is_superadmin ||
                        user.data.permission_pipeline_progress_update !== 'NONE') && (
                          <MenuItem
                            onClick={() => {
                              handleClose()
                              setShowUpdateModal(true)
                              setProgress(row)
                            }}
                          >
                            <i className='fas fa-edit fs-4 me-4'></i>
                            <span>{intl.formatMessage({ id: 'FORM.ACTION.UPDATE' })}</span>
                          </MenuItem>
                        )}

                      {(user.data.is_superadmin ||
                        user.data.permission_pipeline_progress_delete !== 'NONE') && (
                          <MenuItem
                            onClick={() => {
                              handleClose()
                              setShowDeleteModal(true)
                              setProgress(row)
                            }}
                          >
                            <i className='fas fa-trash fs-3 me-5'></i>
                            <span>{intl.formatMessage({ id: 'FORM.ACTION.DELETE' })}</span>
                          </MenuItem>
                        )}
                    </Menu>
                  </>
                )}
            </>
          )
        },
      },
      {
        id: 'name',
        header: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NAME' }),
        meta: {
          size: 1,
          maxSize: 1,
          styleHeader: {
            alignContent: 'center',
          },
          styleBody: {
            alignContent: 'center',
            width: '25%',
          },
        },
        accessorKey: 'name',
      },
      {
        id: 'progress_type',
        header: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PROGRESS_TYPE' }),
        accessorKey: 'progress_type',
      },
      {
        id: 'database_pipeline',
        header: 'Database ' + settings.pipeline_title,
        accessorKey: 'allowedLeadTemplates',
        cell: (props: CellContext<Progress, unknown>) => {
          const row = props.row.original
          if (row.allowedLeadTemplates) {
            if (row.allowedLeadTemplates.length === multiPipelines.length) {
              return <div>
                {intl.formatMessage({ id: "FILTER.SCOPE.PHRASE.ALL" }, { title: intl.formatMessage({ id: "FORM.LABEL.TEMPLATE" }, { title: settings.pipeline_title }) })}
              </div>
            }
            const mergeDatabasePipelineArr = row.allowedLeadTemplates.map((currTemplate) => {
              return currTemplate.name
            }, '')
            const mergeDatabasePipeline = mergeDatabasePipelineArr.join(', ')
            return <div>{mergeDatabasePipeline}</div>
          } else return "-"
        },
        filterFn: (row, _, filterValue) => {
          const templates = row.original.allowedLeadTemplates;

          // Jika filter kosong, tampilkan semua baris
          if (!Array.isArray(filterValue) || filterValue.length === 0) return true;

          // Kalau templates null atau kosong, baris tidak lolos filter
          if (!templates || templates.length === 0) return false;

          const idTemplates = templates.map(t => t.id);

          return idTemplates.some(val => filterValue.includes(val));
        },
      },
      {
        id: 'sub_progress',
        header: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.SUB_PROGRESS' }),
        cell: (props: CellContext<Progress, unknown>) => {
          const row = props.row.original
          {
            return row.sub_progress.length > 0
              ? row.sub_progress.map((data) => data.name).join(', ')
              : '-'
          }
        },
        accessorFn: (row) => {
          return row.sub_progress.length > 0
            ? row.sub_progress.map((data) => data.name).join(', ')
            : '-'
        },
      },
      {
        id: 'color',
        header: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.COLOR' }),
        meta: {
          size: 1,
          maxSize: 1,
          styleHeader: {
            alignContent: 'center',
          },
          styleBody: {
            alignContent: 'center',
            textAlign: 'center',
            width: '75px',
          },
        },
        cell: (props: CellContext<Progress, unknown>) => {
          const row = props.row.original
          return (
            <div
              style={{
                width: '20px',
                height: '20px',
                borderRadius: '6px',
                backgroundColor: row.color,
              }}
            />
          )
        },
        accessorKey: 'color',
      },
      {
        id: 'creator_name',
        header: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.CREATED_BY' }),
        accessorKey: 'creator_name',
      },
      {
        id: 'date_created',
        header: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.DATE_CREATED' }),
        accessorKey: 'date_created',
        accessorFn: (row) => {
          if (row.id === progressId.newPipeline) {
            return '-'
          }
          return moment(row.date_created).format('DD MMM YYYY HH:mm:ss')
        },
      },
    ],
    [intl, openMenuId, multiPipelines]
  )

  let actions: ReactNode[] = []
  if (user.data.permission_pipeline_progress_create !== 'NONE') {
    actions.push(<AddProgress />)
  }
  actions.push(
    <FilterProgresses
      tableKey={tableKey}
      manageColumns={manageColumns}
      setManageColumns={setManageColumns}
    />
  )

  return (
    <>
      <div className='row justify-content-between mb-3'>
        <div className='col-sm-3 col-lg-4 col-xl-5' style={{ alignSelf: 'center' }}>
          <ol className='breadcrumb text-muted fs-6 fw-bold'>
            <li className='breadcrumb-item text-dark'>
              {intl.formatMessage({ id: 'FORM.MENU.PROGRESSES' })}
            </li>
          </ol>
        </div>

        <div className='col-lg-8 col-sm-9 col-xl-7'>
          <div className='d-flex float-end px-3'>
            {isFullyLoaded ? (
              <div
                onClick={() => {
                  mutate(`${API_URL}${table_URL}`)
                  // setFetchCounter(fetchCounter + 1)
                }}
                className='btn btn-light btn-sm ms-2'
              >
                <i className='p-0 fa-sync-alt fas'></i>
              </div>
            ) : (
              <div className='ms-2 my-auto spinner-border spinner-border-sm' role='status'></div>
            )}
          </div>
        </div>
      </div>
      <MasterDataTableTanstack
        tableKey={tableKey}
        tableColumns={columns}
        actions={actions}
        apiURL={table_URL}
        autoReload={false}
        orderBy='date_created'
        orderType='DESC'
      />
      {showUpdateModal && progress && <UpdateProgress progress={progress} />}
      {showDeleteModal && <DeleteProgress progress={progress!} />}
    </>
  )
}

export { MasterProgresses }
